/**
 * usePushNotifications — React hook to manage Web Push subscription state.
 *
 * Returns:
 *   {
 *     supported, permission, subscribed, loading,
 *     iosNeedsPwa,  // true on iOS Safari when not running as PWA
 *     subscribe(), unsubscribe(), sendTest()
 *   }
 *
 * Flow:
 *   1) Check browser support (serviceWorker + PushManager + Notification).
 *   2) Register /sw.js (idempotent).
 *   3) Subscribe via PushManager with the server VAPID public key.
 *   4) POST subscription JSON to /api/push/subscribe.
 *   5) On unsubscribe → POST /api/push/unsubscribe + cancel local sub.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalonePWA() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vapidPublicKey, setVapidPublicKey] = useState(null);
  const [iosNeedsPwa, setIosNeedsPwa] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  // Detect support + iOS standalone
  useEffect(() => {
    const hasSW = typeof navigator !== "undefined" && "serviceWorker" in navigator;
    const hasPush = typeof window !== "undefined" && "PushManager" in window;
    const hasNotif = typeof window !== "undefined" && "Notification" in window;
    const ok = hasSW && hasPush && hasNotif;
    setSupported(ok);
    if (ok) setPermission(Notification.permission);
    const ios = isIOS();
    const standalone = isStandalonePWA();
    setIsIOSDevice(ios);
    setIsStandalone(standalone);
    // iOS Safari: PushManager only available in PWA mode (homescreen install)
    if (ios && !standalone) setIosNeedsPwa(true);
  }, []);

  // Fetch current subscription state from server + Service Worker registration
  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const { data } = await api.get("/push/status");
      setVapidPublicKey(data?.vapid_public_key || null);
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      setSubscribed(!!sub && (data?.active_subscriptions || 0) > 0);
    } catch (e) { /* silently */ }
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: "unsupported" };
    if (iosNeedsPwa) return { ok: false, reason: "ios_pwa_required" };
    setLoading(true);
    try {
      // Ensure SW is registered
      let reg = await navigator.serviceWorker.getRegistration("/sw.js");
      if (!reg) reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // Ask permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setLoading(false);
        return { ok: false, reason: "denied" };
      }

      // Need VAPID key
      let pk = vapidPublicKey;
      if (!pk) {
        const { data } = await api.get("/push/status");
        pk = data?.vapid_public_key;
        setVapidPublicKey(pk);
      }
      if (!pk) {
        setLoading(false);
        return { ok: false, reason: "no_vapid_key" };
      }

      // Subscribe
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(pk),
      });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setSubscribed(true);
      setLoading(false);
      return { ok: true };
    } catch (e) {
      setLoading(false);
      return { ok: false, reason: e?.message || "error" };
    }
  }, [supported, iosNeedsPwa, vapidPublicKey]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
      setLoading(false);
      return { ok: true };
    } catch (e) {
      setLoading(false);
      return { ok: false, reason: e?.message || "error" };
    }
  }, []);

  const sendTest = useCallback(async () => {
    try {
      const { data } = await api.post("/push/test");
      return { ok: data?.sent > 0, data };
    } catch (e) {
      return { ok: false, error: e?.message };
    }
  }, []);

  return {
    supported, permission, subscribed, loading,
    iosNeedsPwa, isIOSDevice, isStandalone, vapidPublicKey,
    subscribe, unsubscribe, sendTest, refresh,
  };
}
